(function (root, factory) {
  const prompt = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = prompt;
  }

  if (root && root.document) {
    prompt.register(root);
  }
})(typeof window !== 'undefined' ? window : undefined, function () {
  'use strict';

  const DISMISSED_KEY = 'mhQuizPromptDismissedUntil';
  const CLICKED_KEY = 'mhQuizPromptClickedUntil';
  const SHOWN_KEY = 'mhQuizPromptShown';

  function readExpiry(storage, key) {
    if (!storage) return 0;

    try {
      return Number(storage.getItem(key)) || 0;
    } catch (_error) {
      return 0;
    }
  }

  function isSuppressed(storage, now) {
    return (
      readExpiry(storage, DISMISSED_KEY) > now ||
      readExpiry(storage, CLICKED_KEY) > now
    );
  }

  function writeSuppression(storage, key, days, now) {
    if (!storage) return;

    try {
      storage.setItem(key, String(now + days * 24 * 60 * 60 * 1000));
    } catch (_error) {
      // The prompt remains dismissible when browser storage is unavailable.
    }
  }

  function getScrollProgress(documentObject) {
    const documentElement = documentObject && documentObject.documentElement;
    if (!documentElement) return 0;

    const available = documentElement.scrollHeight - documentElement.clientHeight;
    if (available <= 0) return 100;

    return Math.min(100, Math.max(0, (documentElement.scrollTop / available) * 100));
  }

  function shouldReveal(delayElapsed, scrollProgress, minimumScroll) {
    return delayElapsed && scrollProgress >= minimumScroll;
  }

  // Shopify's hosted banner and the theme's own consent banner.
  const COOKIE_BANNER_SELECTORS = ['#shopify-pc__banner', '[data-mh-consent-banner]'];

  function isCookieBannerVisible(documentObject, getComputedStyle) {
    if (!documentObject || typeof documentObject.querySelector !== 'function') return false;

    return COOKIE_BANNER_SELECTORS.some((selector) =>
      isBannerElementVisible(documentObject.querySelector(selector), getComputedStyle),
    );
  }

  function isBannerElementVisible(banner, getComputedStyle) {
    if (!banner) return false;

    const style = typeof getComputedStyle === 'function' ? getComputedStyle(banner) : null;
    if (
      style &&
      (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0')
    ) {
      return false;
    }

    return (
      !banner.hidden &&
      banner.getAttribute('aria-hidden') !== 'true' &&
      (typeof banner.getClientRects !== 'function' || banner.getClientRects().length > 0)
    );
  }

  function capture(rootObject, eventName, properties) {
    const posthog = rootObject && rootObject.posthog;
    const status = rootObject && rootObject.document.documentElement.dataset.posthogStatus;

    if (status !== 'active' || !posthog || typeof posthog.capture !== 'function') return;
    posthog.capture(eventName, properties);
  }

  function register(rootObject) {
    if (!rootObject.customElements || rootObject.customElements.get('mh-quiz-prompt')) return;

    class MarloQuizPrompt extends rootObject.HTMLElement {
      connectedCallback() {
        this.closeButton = this.querySelector('[data-quiz-prompt-close]');
        this.cta = this.querySelector('[data-quiz-prompt-cta]');
        if (!this.closeButton || !this.cta) return;

        this.designMode = this.dataset.designMode === 'true';
        this.delayElapsed = this.designMode;
        this.minimumScroll = this.designMode ? 0 : Number(this.dataset.scrollPercentage) || 0;
        this.dismissDays = Number(this.dataset.dismissDays) || 14;
        this.clickedDays = Number(this.dataset.clickedDays) || 30;
        this.disabled = false;

        if (
          !this.designMode &&
          this.dataset.enableMobile !== 'true' &&
          rootObject.matchMedia('(max-width: 749px)').matches
        ) {
          return;
        }

        if (
          !this.designMode &&
          (isSuppressed(rootObject.localStorage, Date.now()) ||
            readExpiry(rootObject.sessionStorage, SHOWN_KEY) > 0)
        ) {
          return;
        }

        this.hidden = false;
        this.handleScroll = () => this.evaluate();
        this.handleConsentCollected = () => rootObject.setTimeout(() => this.evaluate(), 0);
        this.handlePurchaseIntent = (event) => {
          const target = event.target;
          if (!target || typeof target.closest !== 'function') return;
          if (!target.closest('[name="add"], [name="checkout"], a[href^="/cart"]')) return;
          this.disabled = true;
          this.hide();
        };

        this.closeButton.addEventListener('click', () => this.dismiss());
        this.cta.addEventListener('click', () => this.clickThrough());
        rootObject.document.addEventListener('click', this.handlePurchaseIntent);
        rootObject.document.addEventListener('visitorConsentCollected', this.handleConsentCollected);

        if (this.designMode) {
          this.reveal();
          return;
        }

        rootObject.addEventListener('scroll', this.handleScroll, { passive: true });
        this.delayTimer = rootObject.setTimeout(() => {
          this.delayElapsed = true;
          this.evaluate();
        }, (Number(this.dataset.delaySeconds) || 0) * 1000);
      }

      disconnectedCallback() {
        rootObject.clearTimeout(this.delayTimer);
        rootObject.removeEventListener('scroll', this.handleScroll);
        rootObject.document.removeEventListener('click', this.handlePurchaseIntent);
        rootObject.document.removeEventListener(
          'visitorConsentCollected',
          this.handleConsentCollected,
        );
      }

      evaluate() {
        if (this.disabled || this.classList.contains('is-visible')) return;
        if (isCookieBannerVisible(rootObject.document, rootObject.getComputedStyle)) return;
        if (!shouldReveal(this.delayElapsed, getScrollProgress(rootObject.document), this.minimumScroll)) {
          return;
        }
        this.reveal();
      }

      reveal() {
        if (this.disabled) return;
        this.classList.add('is-visible');

        if (!this.designMode) {
          writeSuppression(rootObject.sessionStorage, SHOWN_KEY, 1, 0);
          capture(rootObject, 'Quiz Prompt Viewed', {
            surface: 'storefront_floating_card',
            page_type: this.dataset.pageType,
          });
        }
      }

      hide() {
        this.classList.remove('is-visible');
      }

      dismiss() {
        this.hide();
        if (this.designMode) return;

        this.disabled = true;
        writeSuppression(rootObject.localStorage, DISMISSED_KEY, this.dismissDays, Date.now());
        capture(rootObject, 'Quiz Prompt Dismissed', {
          surface: 'storefront_floating_card',
          page_type: this.dataset.pageType,
        });
      }

      clickThrough() {
        if (this.designMode) return;

        this.disabled = true;
        this.hide();
        writeSuppression(rootObject.localStorage, CLICKED_KEY, this.clickedDays, Date.now());
        capture(rootObject, 'Quiz Prompt Clicked', {
          surface: 'storefront_floating_card',
          page_type: this.dataset.pageType,
          destination: '/quiz',
        });
      }
    }

    rootObject.customElements.define('mh-quiz-prompt', MarloQuizPrompt);
  }

  return {
    getScrollProgress,
    isCookieBannerVisible,
    isSuppressed,
    readExpiry,
    register,
    shouldReveal,
    writeSuppression,
  };
});
